import React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { childrenShape } from '@bibliocommons/bc-prop-types';
import { TOGGLZ_FEATURES } from '@bibliocommons/constants-feature-togglz';

import { trackAnalyticsEvent } from 'app/actions/AnalyticsGa4Actions';
import ErrorPage from 'app/components/ErrorPage';
import { DocumentHeadContainer } from 'app/components/shared/DocumentHead';
import MobileMenuOverlay from 'app/components/shared/MobileMenuOverlay';
import {
  eventNames,
  METADATA_CATEGORY_TYPE,
  UI_COMPONENT_TYPE,
  UI_CONTAINER_TYPE
} from 'app/constants/AnalyticsGa4Constants';
import {
  BROWSE_HOME_PAGE_PATH,
  BROWSE_TITLES_PAGE_FACET_FIELD_IDS,
  NAV_ITEM_ID,
  SUB_PAGE_BASE_API_PARAMS,
  SUB_PAGE_SLUG
} from 'app/constants/BrowseConstants';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { MENU_ITEM_LINK_TYPE } from 'app/constants/menuConstants';
import { makeTabLinkUrl } from 'app/helpers/browse';
import { deserializeQuerystring, serializeQuerystring } from 'app/helpers/browse/browseQueryHelpers';
import { mapDisplayBibToAnalyticsBibEntity } from 'app/helpers/displayBib';
import useBrowseFacets from 'app/hooks/useBrowseFacets';
import useFeature from 'app/hooks/useFeature';
import { selectCurrentLibrary } from 'app/selectors/LibrarySelector';
import BrowseTitlesPage from './BrowseTitlesPage';
import FilterableDisplayBibGrid from '../../shared/FilterableDisplayBibGrid';

const MOBILE_MENU_OVERLAY_KEY = 'browse-titles-mobile-menu-overlay';
const PATH_PREFIX = '/v2/browse/titles';
const CATALOG_SEARCH_PATH_PREFIX = '/v2/search?';

const BrowseTitlesPageContainer = ({ children }) => {
  const { pathname, search } = useLocation();
  const isOnOrderPage = pathname.endsWith(`/${SUB_PAGE_SLUG.ON_ORDER}`);
  const baseApiParams = SUB_PAGE_BASE_API_PARAMS[isOnOrderPage ? SUB_PAGE_SLUG.ON_ORDER : SUB_PAGE_SLUG.NEW];
  const catalogSearchLink = {
    url: `${CATALOG_SEARCH_PATH_PREFIX}${new URLSearchParams({
      ...baseApiParams,
      limit: baseApiParams.limit.toString()
    }).toString()}`,
    text: isOnOrderPage ? 'View on order titles in Search' : 'View new titles in Search'
  };

  const {
    displayAvailabilityByMetadataId,
    displayBibs,
    facetFields,
    handleFilterChange,
    isFetching,
    pagination,
    setShowMoreFieldId,
    unapplyAllFilters
  } = useBrowseFacets({
    baseApiParams,
    baseQuerystring: '',
    deserializeQuerystring,
    includedFieldIds: BROWSE_TITLES_PAGE_FACET_FIELD_IDS,
    serializeQuerystring
  });

  const dispatch = useDispatch();
  const intl = useIntl();
  const isCoreV2NewAndOnOrderPagesEnabled = useFeature(TOGGLZ_FEATURES.CORE_V2_NEW_AND_ON_ORDER_PAGES);
  const currentLibrary = useSelector(selectCurrentLibrary);

  if (!isCoreV2NewAndOnOrderPagesEnabled) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  const appliedFiltersAccessibilityHeading = intl.formatMessage({ id: 'browse_active_filters' });

  const facetsHeading = intl.formatMessage({
    defaultMessage: 'Browse filters',
    id: 'browse_facets'
  });

  const searchParams = new URLSearchParams(search);
  const tabLinks = [
    {
      id: SUB_PAGE_SLUG.NEW,
      isActive: pathname.endsWith(`/${SUB_PAGE_SLUG.NEW}`),
      text: intl.formatMessage({ defaultMessage: 'New titles', id: 'browse.nav.new' }),
      to: makeTabLinkUrl({ path: `${PATH_PREFIX}/${SUB_PAGE_SLUG.NEW}`, searchParams })
    },
    {
      id: SUB_PAGE_SLUG.ON_ORDER,
      isActive: pathname.endsWith(`/${SUB_PAGE_SLUG.ON_ORDER}`),
      text: intl.formatMessage({ defaultMessage: 'On order titles', id: 'browse.nav.on_order' }),
      to: makeTabLinkUrl({ path: `${PATH_PREFIX}/${SUB_PAGE_SLUG.ON_ORDER}`, searchParams })
    }
  ];

  const NAV_ITEM_BY_ID = {
    [NAV_ITEM_ID.AWARDS]: {
      id: NAV_ITEM_ID.AWARDS,
      isActive: false,
      text: intl.formatMessage({ defaultMessage: 'Award winners', id: 'browse.nav.awards' }),
      url: '/explore/awards'
    },
    [NAV_ITEM_ID.BEST_SELLERS]: {
      id: NAV_ITEM_ID.BEST_SELLERS,
      isActive: false,
      text: intl.formatMessage({ defaultMessage: 'Best sellers', id: 'browse.nav.best_sellers' }),
      url: '/explore/bestsellers'
    },
    [NAV_ITEM_ID.LANGUAGES]: {
      id: NAV_ITEM_ID.LANGUAGES,
      isActive: false,
      text: intl.formatMessage({ defaultMessage: 'World languages', id: 'browse.nav.languages' }),
      url: '/explore/featured_lists/languages'
    },
    [NAV_ITEM_ID.NEW_AND_ON_ORDER]: {
      id: NAV_ITEM_ID.NEW_AND_ON_ORDER,
      isActive: true,
      text: intl.formatMessage({ defaultMessage: 'New and on order', id: 'browse.nav.new_and_on_order' }),
      url: `${PATH_PREFIX}/${SUB_PAGE_SLUG.NEW}`
    },
    [NAV_ITEM_ID.STAFF_PICKS]: {
      id: NAV_ITEM_ID.STAFF_PICKS,
      isActive: false,
      text: intl.formatMessage({ defaultMessage: 'Staff picks', id: 'browse.nav.staff_picks' }),
      url: '/explore/featured_lists/staff_picks'
    },
    [NAV_ITEM_ID.TEACHER_PICKS]: {
      id: NAV_ITEM_ID.TEACHER_PICKS,
      isActive: false,
      text: intl.formatMessage({ defaultMessage: 'Teacher picks', id: 'browse.nav.teacher_picks' }),
      url: '/explore/featured_lists/teacher_picks'
    }
  };
  const navSettings = currentLibrary.getIn(['browse', 'nav'])?.toJS() ?? {};
  const navItems = [
    NAV_ITEM_ID.NEW_AND_ON_ORDER,
    NAV_ITEM_ID.AWARDS,
    NAV_ITEM_ID.BEST_SELLERS,
    NAV_ITEM_ID.STAFF_PICKS,
    NAV_ITEM_ID.LANGUAGES,
    NAV_ITEM_ID.TEACHER_PICKS
  ]
    .filter(navItemId => navItemId === NAV_ITEM_ID.NEW_AND_ON_ORDER || navSettings[`has${navItemId}`])
    .map(navItemId => NAV_ITEM_BY_ID[navItemId]);

  const mobileMenuItems = navItems.map(navItem => {
    const { isActive, text: label, url } = navItem;

    return {
      isActive,
      isExpanded: false,
      label,
      linkType: url.startsWith(PATH_PREFIX) ? MENU_ITEM_LINK_TYPE.HISTORY : MENU_ITEM_LINK_TYPE.STANDARD,
      url
    };
  });

  const mobileMenuOverlayTitle = intl.formatMessage({ defaultMessage: 'Browse', id: 'browse' });
  const pageTitle = intl.formatMessage({ defaultMessage: 'New and on order', id: 'browse.nav.new_and_on_order' });

  const renderMobileMenuOverlay = () => {
    return (
      <MobileMenuOverlay
        menuItems={mobileMenuItems}
        overlayKey={MOBILE_MENU_OVERLAY_KEY}
        title={mobileMenuOverlayTitle}
        triggerLabel={pageTitle}
      />
    );
  };

  const trackAuthorClick = ({ author, displayBib, itemPosition }) => {
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId];

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_CREATORS,
            metadata_value: author
          },
          ui: {
            ui_component_label: author,
            ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: itemPosition,
            ui_content_total_count: numTotalItems
          }
        },
        eventName: eventNames.CATALOG_EXPLORE
      })
    );
  };

  const trackBibLinkClick = (displayBib, title, isImage, itemPosition) => {
    const { count: numTotalItems, page } = pagination?.toJS() ?? {};

    const { metadataId } = displayBib;
    const displayAvailability = displayAvailabilityByMetadataId[metadataId];

    dispatch(
      trackAnalyticsEvent({
        entities: {
          bib: mapDisplayBibToAnalyticsBibEntity(displayBib, displayAvailability),
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.BIBLIOGRAPHIC_DETAILS_TITLE,
            metadata_value: title
          },
          ui: {
            ...(isImage ? {} : { ui_component_label: title }),
            ui_component_type: isImage ? UI_COMPONENT_TYPE.LINK_IMAGE : UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.ITEM,
            ui_content_page: page,
            ui_content_position: itemPosition,
            ui_content_total_count: numTotalItems
          }
        },
        eventName: eventNames.CONTENT_CLICK
      })
    );
  };

  const renderFilterableDisplayBibGrid = () => {
    return (
      <FilterableDisplayBibGrid
        appliedFiltersAccessibilityHeading={appliedFiltersAccessibilityHeading}
        catalogSearchLink={catalogSearchLink}
        displayBibs={displayBibs}
        facetFields={facetFields}
        facetsHeading={facetsHeading}
        handleFilterChange={handleFilterChange}
        isFetching={isFetching}
        pagination={pagination}
        setShowMoreFieldId={setShowMoreFieldId}
        trackAuthorClick={trackAuthorClick}
        trackBibLinkClick={trackBibLinkClick}
        unapplyAllFilters={unapplyAllFilters}
      />
    );
  };

  const trackNavLinkClick = (url, text) => {
    dispatch(
      trackAnalyticsEvent({
        entities: {
          metadata: {
            metadata_category_type: METADATA_CATEGORY_TYPE.NAVIGATION,
            metadata_value: url
          },
          ui: {
            ui_component_label: text,
            ui_component_type: UI_COMPONENT_TYPE.LINK_TEXT,
            ui_container_type: UI_CONTAINER_TYPE.BROWSE_TITLES_NAV
          }
        },
        eventName: eventNames.NAVIGATE
      })
    );
  };

  const breadcrumbLinks = [
    {
      label: intl.formatMessage({ id: 'browse' }),
      url: BROWSE_HOME_PAGE_PATH
    },
    pathname.endsWith(`/${SUB_PAGE_SLUG.NEW}`)
      ? {
          label: intl.formatMessage({ defaultMessage: 'New titles', id: 'browse.nav.new' }),
          url: makeTabLinkUrl({ path: `${PATH_PREFIX}/${SUB_PAGE_SLUG.NEW}`, searchParams })
        }
      : {
          label: intl.formatMessage({ defaultMessage: 'On order titles', id: 'browse.nav.on_order' }),
          url: makeTabLinkUrl({ path: `${PATH_PREFIX}/${SUB_PAGE_SLUG.ON_ORDER}`, searchParams })
        }
  ];

  if (!facetFields) {
    return null;
  }
  return (
    <>
      <DocumentHeadContainer pageTitle={pageTitle} />
      <BrowseTitlesPage
        breadcrumbLinks={breadcrumbLinks}
        navItems={navItems}
        pageTitle={pageTitle}
        renderFilterableDisplayBibGrid={renderFilterableDisplayBibGrid}
        renderMobileMenuOverlay={renderMobileMenuOverlay}
        tabLinks={tabLinks}
        trackNavLinkClick={trackNavLinkClick}
      >
        {children}
      </BrowseTitlesPage>
    </>
  );
};

BrowseTitlesPageContainer.propTypes = {
  children: childrenShape.isRequired
};

export default BrowseTitlesPageContainer;
